import { create } from 'zustand';
import type { ResponsiveLayouts, LayoutItem } from 'react-grid-layout';

const genId = () => `w-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

export type WidgetType = 'kpi' | 'category' | 'alerts' | 'activity' | 'stock_by_item' | 'stock_by_item_counts';
export type KpiMetric = 'total_items' | 'checked_out' | 'pending_requests' | 'inventory_value';
export type ScopeMode = 'global' | 'per_widget';
export type ScopeType = 'inherit' | 'warehouse' | 'storage_area';

export interface WidgetConfig {
  id: string;
  type: WidgetType;
  title: string;
  accentColor: string;
  bgTint: string;
  kpiMetric?: KpiMetric;
  /** Catalogue `items.id` for item-based stock widgets */
  stockCatalogueItemId?: string | null;
  scopeType?: ScopeType;
  scopeWarehouseId?: string | null;
  scopeStorageAreaId?: string | null;
}

export const ACCENT_COLORS: Record<string, string> = {
  blue: '#3b82f6',
  amber: '#f59e0b',
  orange: '#f97316',
  emerald: '#10b981',
  red: '#ef4444',
  purple: '#8b5cf6',
  teal: '#14b8a6',
  pink: '#ec4899',
  indigo: '#6366f1',
  gray: '#6b7280',
};

const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: 'kpi-1', type: 'kpi', title: 'Total Items',       accentColor: ACCENT_COLORS.blue,    bgTint: '', kpiMetric: 'total_items', scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null },
  { id: 'kpi-2', type: 'kpi', title: 'Checked Out',       accentColor: ACCENT_COLORS.amber,   bgTint: '', kpiMetric: 'checked_out', scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null },
  { id: 'kpi-3', type: 'kpi', title: 'Pending Requests',  accentColor: ACCENT_COLORS.orange,  bgTint: '', kpiMetric: 'pending_requests', scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null },
  { id: 'kpi-4', type: 'kpi', title: 'Inventory Value',   accentColor: ACCENT_COLORS.emerald, bgTint: '', kpiMetric: 'inventory_value', scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null },
  { id: 'cat-1',      type: 'category', title: 'Stock by Category', accentColor: ACCENT_COLORS.blue,  bgTint: '', scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null },
  { id: 'alerts-1',   type: 'alerts',   title: 'Alerts',             accentColor: ACCENT_COLORS.amber, bgTint: '', scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null },
  { id: 'activity-1', type: 'activity', title: 'Recent Activity',    accentColor: ACCENT_COLORS.gray,  bgTint: '', scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null },
];

type LayoutRow = LayoutItem & { minW?: number; minH?: number };

const DEFAULT_LAYOUTS: ResponsiveLayouts = {
  lg: [
    { i: 'kpi-1',      x: 0,  y: 0, w: 3, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-2',      x: 3,  y: 0, w: 3, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-3',      x: 6,  y: 0, w: 3, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-4',      x: 9,  y: 0, w: 3, h: 2, minW: 2, minH: 2 },
    { i: 'cat-1',      x: 0,  y: 2, w: 6, h: 5, minW: 3, minH: 3 },
    { i: 'alerts-1',   x: 6,  y: 2, w: 6, h: 5, minW: 3, minH: 3 },
    { i: 'activity-1', x: 0,  y: 7, w: 12, h: 6, minW: 4, minH: 3 },
  ] as LayoutRow[],
  md: [
    { i: 'kpi-1',      x: 0, y: 0, w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-2',      x: 2, y: 0, w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-3',      x: 4, y: 0, w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-4',      x: 6, y: 0, w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'cat-1',      x: 0, y: 2, w: 4, h: 5, minW: 3, minH: 3 },
    { i: 'alerts-1',   x: 4, y: 2, w: 4, h: 5, minW: 3, minH: 3 },
    { i: 'activity-1', x: 0, y: 7, w: 8, h: 6, minW: 4, minH: 3 },
  ] as LayoutRow[],
  sm: [
    { i: 'kpi-1',      x: 0, y: 0,  w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-2',      x: 2, y: 0,  w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-3',      x: 0, y: 2,  w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'kpi-4',      x: 2, y: 2,  w: 2, h: 2, minW: 2, minH: 2 },
    { i: 'cat-1',      x: 0, y: 4,  w: 4, h: 5, minW: 3, minH: 3 },
    { i: 'alerts-1',   x: 0, y: 9,  w: 4, h: 5, minW: 3, minH: 3 },
    { i: 'activity-1', x: 0, y: 14, w: 4, h: 6, minW: 4, minH: 3 },
  ] as LayoutRow[],
};

export const WIDGET_DIMS: Record<WidgetType, { w: number; h: number; minW: number; minH: number }> = {
  kpi:      { w: 3,  h: 2, minW: 2, minH: 2 },
  category: { w: 6,  h: 5, minW: 3, minH: 3 },
  alerts:   { w: 6,  h: 5, minW: 3, minH: 3 },
  activity: { w: 12, h: 6, minW: 4, minH: 3 },
  stock_by_item: { w: 2, h: 1, minW: 1, minH: 1 },
  stock_by_item_counts: { w: 2, h: 1, minW: 1, minH: 1 },
};

export const WIDGET_DEFAULT_TITLES: Record<WidgetType, string> = {
  kpi:      'KPI Stat',
  category: 'Stock by Category',
  alerts:   'Alerts',
  activity: 'Recent Activity',
  stock_by_item: 'Item Inventory Detail',
  stock_by_item_counts: 'Stock by Item',
};

const STORAGE_KEY = 'netprobuilder_stock_dashboard_v2';

type PersistedDashboard = {
  widgets: WidgetConfig[];
  layouts: ResponsiveLayouts;
  scopeMode: ScopeMode;
  globalScopeWarehouseId: string | null;
  globalScopeStorageAreaId: string | null;
};

function withWidgetScopeDefaults(widgets: WidgetConfig[]): WidgetConfig[] {
  return widgets.map(widget => ({
    ...widget,
    title: widget.type === 'stock_by_item' && widget.title === 'Stock by Item'
      ? WIDGET_DEFAULT_TITLES.stock_by_item
      : widget.title,
    scopeType: widget.scopeType ?? 'inherit',
    scopeWarehouseId: widget.scopeWarehouseId ?? null,
    scopeStorageAreaId: widget.scopeStorageAreaId ?? null,
    stockCatalogueItemId: widget.stockCatalogueItemId ?? null,
  }));
}

/** Let compact tiles resize down after we lowered stock_by_item grid mins. */
function patchStockByItemLayoutMins(layouts: ResponsiveLayouts, widgets: WidgetConfig[]): ResponsiveLayouts {
  const ids = new Set(
    widgets
      .filter(w => w.type === 'stock_by_item' || w.type === 'stock_by_item_counts')
      .map(w => w.id)
  );
  if (ids.size === 0) return layouts;
  const next: ResponsiveLayouts = { ...layouts };
  for (const key of Object.keys(next)) {
    const row = (next as Record<string, LayoutItem[]>)[key];
    if (!row) continue;
    (next as Record<string, LayoutItem[]>)[key] = row.map(item => {
      if (!ids.has(item.i)) return item;
      return { ...item, minW: 1, minH: 1 };
    });
  }
  return next;
}

function loadFromStorage(): PersistedDashboard | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedDashboard>;
      const widgets = withWidgetScopeDefaults(parsed.widgets ?? DEFAULT_WIDGETS);
      return {
        widgets,
        layouts: patchStockByItemLayoutMins(parsed.layouts ?? DEFAULT_LAYOUTS, widgets),
        scopeMode: parsed.scopeMode ?? 'global',
        globalScopeWarehouseId: parsed.globalScopeWarehouseId ?? null,
        globalScopeStorageAreaId: parsed.globalScopeStorageAreaId ?? null,
      };
    }
  } catch { /* ignore */ }
  return null;
}

function saveToStorage(
  widgets: WidgetConfig[],
  layouts: ResponsiveLayouts,
  scopeMode: ScopeMode,
  globalScopeWarehouseId: string | null,
  globalScopeStorageAreaId: string | null,
) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      widgets,
      layouts,
      scopeMode,
      globalScopeWarehouseId,
      globalScopeStorageAreaId,
    }));
  } catch { /* ignore */ }
}

interface StockDashboardState {
  widgets: WidgetConfig[];
  layouts: ResponsiveLayouts;
  isEditMode: boolean;
  activeConfigWidgetId: string | null;
  scopeMode: ScopeMode;
  globalScopeWarehouseId: string | null;
  globalScopeStorageAreaId: string | null;
  _snap: {
    widgets: WidgetConfig[];
    layouts: ResponsiveLayouts;
    scopeMode: ScopeMode;
    globalScopeWarehouseId: string | null;
    globalScopeStorageAreaId: string | null;
  } | null;

  enterEditMode: () => void;
  exitEditMode: (save: boolean) => void;
  updateLayouts: (layouts: ResponsiveLayouts) => void;
  addWidget: (type: WidgetType) => void;
  removeWidget: (id: string) => void;
  updateWidgetConfig: (id: string, patch: Partial<WidgetConfig>) => void;
  setScopeMode: (mode: ScopeMode) => void;
  setGlobalScope: (warehouseId: string | null, storageAreaId: string | null) => void;
  setActiveConfigWidget: (id: string | null) => void;
  resetToDefaults: () => void;
}

const stored = loadFromStorage();

export const useStockDashboardStore = create<StockDashboardState>((set, get) => ({
  widgets: stored?.widgets ?? DEFAULT_WIDGETS,
  layouts: stored?.layouts ?? DEFAULT_LAYOUTS,
  isEditMode: false,
  activeConfigWidgetId: null,
  scopeMode: stored?.scopeMode ?? 'global',
  globalScopeWarehouseId: stored?.globalScopeWarehouseId ?? null,
  globalScopeStorageAreaId: stored?.globalScopeStorageAreaId ?? null,
  _snap: null,

  enterEditMode: () => {
    const { widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId } = get();
    set({
      isEditMode: true,
      activeConfigWidgetId: null,
      _snap: {
        widgets: JSON.parse(JSON.stringify(widgets)),
        layouts: JSON.parse(JSON.stringify(layouts)),
        scopeMode,
        globalScopeWarehouseId,
        globalScopeStorageAreaId,
      },
    });
  },

  exitEditMode: (save) => {
    if (save) {
      const { widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId } = get();
      saveToStorage(widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId);
      set({ isEditMode: false, activeConfigWidgetId: null, _snap: null });
    } else {
      const snap = get()._snap;
      set({
        widgets: snap?.widgets ?? get().widgets,
        layouts: snap?.layouts ?? get().layouts,
        scopeMode: snap?.scopeMode ?? get().scopeMode,
        globalScopeWarehouseId: snap?.globalScopeWarehouseId ?? get().globalScopeWarehouseId,
        globalScopeStorageAreaId: snap?.globalScopeStorageAreaId ?? get().globalScopeStorageAreaId,
        isEditMode: false,
        activeConfigWidgetId: null,
        _snap: null,
      });
    }
  },

  updateLayouts: (layouts) => set({ layouts }),

  addWidget: (type) => {
    const id = genId();
    const newWidget: WidgetConfig = {
      id,
      type,
      title: WIDGET_DEFAULT_TITLES[type],
      accentColor: ACCENT_COLORS.blue,
      bgTint: '',
      scopeType: 'inherit',
      scopeWarehouseId: null,
      scopeStorageAreaId: null,
      stockCatalogueItemId: null,
      ...(type === 'kpi' ? { kpiMetric: 'total_items' as KpiMetric } : {}),
    };

    const { widgets, layouts } = get();
    const dims = WIDGET_DIMS[type];
    const breakpoints = ['lg', 'md', 'sm', 'xs', 'xxs'] as const;
    const newLayouts: ResponsiveLayouts = { ...layouts };

    breakpoints.forEach(bp => {
      const existing = (newLayouts[bp] as LayoutItem[] | undefined) ?? [];
      const maxY = existing.reduce((acc: number, l: LayoutItem) => Math.max(acc, l.y + l.h), 0);
      newLayouts[bp] = [...existing, { i: id, x: 0, y: maxY, ...dims }];
    });

    set({ widgets: [...widgets, newWidget], layouts: newLayouts });
  },

  removeWidget: (id) => {
    const { widgets, layouts, activeConfigWidgetId } = get();
    const newLayouts: ResponsiveLayouts = {};
    Object.keys(layouts).forEach(bp => {
      const bpItems = (layouts as Record<string, LayoutItem[]>)[bp] ?? [];
      (newLayouts as Record<string, LayoutItem[]>)[bp] = bpItems.filter((l: LayoutItem) => l.i !== id);
    });
    set({
      widgets: widgets.filter(w => w.id !== id),
      layouts: newLayouts,
      activeConfigWidgetId: activeConfigWidgetId === id ? null : activeConfigWidgetId,
    });
  },

  updateWidgetConfig: (id, patch) => {
    set({ widgets: get().widgets.map(w => w.id === id ? { ...w, ...patch } : w) });
    if (!get().isEditMode) {
      const { widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId } = get();
      saveToStorage(widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId);
    }
  },

  setScopeMode: (mode) => {
    set({ scopeMode: mode });
    const { widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId } = get();
    saveToStorage(widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId);
  },

  setGlobalScope: (warehouseId, storageAreaId) => {
    set({ globalScopeWarehouseId: warehouseId, globalScopeStorageAreaId: storageAreaId });
    const { widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId } = get();
    saveToStorage(widgets, layouts, scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId);
  },

  setActiveConfigWidget: (id) => set({ activeConfigWidgetId: id }),

  resetToDefaults: () => {
    saveToStorage(DEFAULT_WIDGETS, DEFAULT_LAYOUTS, 'global', null, null);
    set({
      widgets: DEFAULT_WIDGETS,
      layouts: DEFAULT_LAYOUTS,
      scopeMode: 'global',
      globalScopeWarehouseId: null,
      globalScopeStorageAreaId: null,
    });
  },
}));
